import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiCopy, FiAlertTriangle } = FiIcons;

const DuplicateDetection = ({ questions }) => {
  const [duplicates, setDuplicates] = useState([]);
  const [nearDuplicates, setNearDuplicates] = useState([]);
  const [hasDuplicates, setHasDuplicates] = useState(false);

  useEffect(() => {
    if (!questions.length) return;
    
    // Find exact duplicates (ignoring case and punctuation)
    const normalizedQuestions = questions.map(q => ({
      id: q.id,
      text: q.text,
      normalized: normalizeText(q.text)
    }));
    
    const exactDuplicates = [];
    const textMap = {};
    
    normalizedQuestions.forEach(q => {
      if (textMap[q.normalized]) {
        // Found a duplicate
        const duplicateGroup = textMap[q.normalized];
        duplicateGroup.items.push({
          id: q.id,
          text: q.text
        });
      } else {
        // First occurrence
        textMap[q.normalized] = {
          normalized: q.normalized,
          items: [{
            id: q.id,
            text: q.text
          }]
        };
      }
    });
    
    // Filter groups with more than one item
    Object.values(textMap)
      .filter(group => group.items.length > 1)
      .forEach(group => exactDuplicates.push(group));
    
    setDuplicates(exactDuplicates);
    
    // Find near-duplicates using similarity
    const nearDuplicateGroups = [];
    const threshold = 0.8; // 80% similarity threshold
    
    for (let i = 0; i < normalizedQuestions.length; i++) {
      for (let j = i + 1; j < normalizedQuestions.length; j++) {
        const sim = calculateSimilarity(
          normalizedQuestions[i].normalized,
          normalizedQuestions[j].normalized
        );
        
        if (sim >= threshold && sim < 1.0) { // Not exact match but very similar
          nearDuplicateGroups.push({
            similarity: sim,
            items: [
              {
                id: normalizedQuestions[i].id,
                text: normalizedQuestions[i].text
              },
              {
                id: normalizedQuestions[j].id,
                text: normalizedQuestions[j].text
              }
            ]
          });
        }
      }
    }
    
    setNearDuplicates(nearDuplicateGroups);
    setHasDuplicates(exactDuplicates.length > 0 || nearDuplicateGroups.length > 0);
    
  }, [questions]);
  
  // Function to normalize text for comparison
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s]/g, '') // Remove punctuation
      .replace(/\s+/g, ' ')    // Normalize whitespace
      .trim();
  };
  
  // Calculate similarity between two strings (Jaccard similarity of words)
  const calculateSimilarity = (a, b) => {
    const setA = new Set(a.split(' '));
    const setB = new Set(b.split(' '));
    
    // Calculate intersection
    const intersection = new Set([...setA].filter(x => setB.has(x)));
    
    // Calculate union
    const union = new Set([...setA, ...setB]);
    
    return union.size === 0 ? 0 : intersection.size / union.size;
  };
  
  // Helper function to highlight differences between texts
  const highlightDifferences = (text1, text2) => {
    // Simplified difference highlighting (not a full diff algorithm)
    const words1 = text1.split(' ');
    const words2 = text2.split(' ');
    
    // Just show both texts for now
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-700">{text1}</p>
        <p className="text-sm text-gray-700">{text2}</p>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
      <div className="flex items-center space-x-2 mb-6">
        <SafeIcon icon={FiCopy} className="text-primary-600" />
        <h3 className="text-lg font-semibold text-gray-800">Duplicate Detection</h3>
      </div>
      
      {!hasDuplicates ? (
        <div className="bg-green-50 p-6 rounded-lg text-center">
          <SafeIcon icon={FiCopy} className="mx-auto text-4xl text-green-500 mb-3" />
          <h4 className="font-medium text-gray-800 mb-2">No Duplicates Found</h4>
          <p className="text-sm text-gray-600">
            All questions in your question bank appear to be unique. Good job maintaining diversity!
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Exact Duplicates */}
          {duplicates.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <SafeIcon icon={FiAlertTriangle} className="text-red-500" />
                <h4 className="font-medium text-gray-800">Exact Duplicates ({duplicates.length} groups)</h4>
              </div>
              
              <div className="space-y-4">
                {duplicates.map((group, index) => (
                  <div key={index} className="bg-red-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">
                      Duplicate Group #{index + 1} ({group.items.length} items)
                    </h5>
                    <div className="space-y-2">
                      {group.items.map(item => (
                        <div key={item.id} className="bg-white p-3 rounded border border-red-200">
                          <p className="text-sm text-gray-700">{item.text}</p>
                          <p className="text-xs text-gray-500 mt-1">ID: {item.id}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Near Duplicates */}
          {nearDuplicates.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
                <h4 className="font-medium text-gray-800">Near-Duplicates ({nearDuplicates.length} pairs)</h4>
              </div>
              
              <div className="space-y-4">
                {nearDuplicates.map((pair, index) => (
                  <div key={index} className="bg-yellow-50 p-4 rounded-lg">
                    <h5 className="font-medium text-gray-800 mb-2">
                      Similar Pair #{index + 1} ({Math.round(pair.similarity * 100)}% similar)
                    </h5>
                    <div className="bg-white p-3 rounded border border-yellow-200">
                      {highlightDifferences(pair.items[0].text, pair.items[1].text)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="mt-8 bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-gray-800 mb-2">About Duplicate Detection</h4>
        <p className="text-sm text-gray-600">
          This analysis identifies both exact duplicates (identical questions after normalization) and
          near-duplicates (highly similar questions with minor differences). Duplicates in your question
          bank can lead to repetitive assessments and unfair advantages for students who recognize
          repeated questions. Consider reviewing and consolidating duplicates to maintain question bank quality.
        </p>
      </div>
    </div>
  );
};

export default DuplicateDetection;